import { dehydrate, hydrate, type QueryClient, type Query } from '@tanstack/react-query';

/**
 * THE APP REMEMBERS WHAT IT HAS ALREADY SEEN, ACROSS RELOADS.
 *
 * TanStack Query already shows a screen's cached data instantly and
 * refreshes behind it — but only for as long as the page lives. Every reload
 * threw it all away: opening the app, coming back from a game (the player is
 * a page of its own, so returning is a reload), switching profile. Each of
 * those started from nothing, so every row waited on the network again and
 * the app felt like a slow connection even on the LAN.
 *
 * So the data that draws the main screens is written to localStorage and put
 * back on start. A screen then paints from it at once, and the ordinary
 * staleness rules decide whether to refresh behind it — which is how Plex
 * behaves: last time's rows first, fresh ones a moment later.
 *
 * WHAT IS KEPT is a list, not "everything": the rows and grids people move
 * between. Not `me` (who you are must always come from the server), not
 * search, not the guide (it is a function of the clock).
 *
 * PER PERSON AND PER BUILD. The saved copy carries the id of the profile it
 * belongs to, and is thrown away when anyone else signs in — nobody sees a
 * frame of somebody else's Continue Watching. It also carries the build, so
 * a new client never reads data shaped for the old one.
 */

const STORE_KEY = 'n8m-cache-v1';
/** localStorage holds ~5 MB per origin; stay well inside it. */
const MAX_CHARS = 3_000_000;
/** Older than this is not worth painting even as a placeholder. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const KEPT: ReadonlyArray<ReadonlyArray<string>> = [
  ['library', 'home'],
  ['library', 'sections'],
  ['library', 'browse'],
  ['library', 'item'],
  ['library', 'children'],
  ['games'],
  ['channels'],
  ['groups'],
];

/**
 * Kept second-tier: dropped first when the whole lot will not fit. A
 * five-thousand-film section walked to the bottom is megabytes on its own,
 * and the home rows matter more.
 */
const BULKY = new Set(['browse', 'children', 'item']);

/**
 * Which build of the client this is. The bundle's own file name carries a
 * content hash (`/assets/index-3f9c.js`), so it changes exactly when the code
 * does and needs nobody to remember to bump a number.
 */
const BUILD = (() => {
  try { return new URL(import.meta.url).pathname; } catch { return 'dev'; }
})();

interface Saved { user: string; build: string; at: number; state: unknown }

function kept(q: Query, withBulky: boolean): boolean {
  if (q.state.status !== 'success') return false;
  const k = q.queryKey as unknown[];
  return KEPT.some((prefix) =>
    prefix.every((p, i) => k[i] === p)
    && (withBulky || !BULKY.has(String(k[1]))));
}

function read(): Saved | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) as Saved : null;
  } catch {
    return null;
  }
}

/** Forget the saved copy — on sign-out and on switching profile. */
export function clearSavedCache(): void {
  try { localStorage.removeItem(STORE_KEY); } catch { /* private mode, full disk */ }
}

/**
 * Put the saved copy back, for this person, before anything renders from it.
 * Returns whether anything was restored.
 */
export function restoreSavedCache(qc: QueryClient, userId: string): boolean {
  const saved = read();
  if (!saved) return false;
  if (saved.user !== userId || saved.build !== BUILD || Date.now() - saved.at > MAX_AGE_MS) {
    clearSavedCache();
    return false;
  }
  try {
    hydrate(qc, saved.state as any);
    return true;
  } catch {
    clearSavedCache();
    return false;
  }
}

/**
 * Keep the saved copy up to date from now on. Debounced — the cache changes
 * on every fetch, and serialising it is not free on a Fire TV Stick — and
 * flushed immediately when the page is about to go (into a game, into the
 * background), because that is precisely when it is needed next.
 */
export function startSavingCache(qc: QueryClient, userId: string): () => void {
  let timer = 0;

  const save = () => {
    window.clearTimeout(timer);
    timer = 0;
    for (const withBulky of [true, false]) {
      try {
        const state = dehydrate(qc, { shouldDehydrateQuery: (q) => kept(q, withBulky) });
        const text = JSON.stringify({ user: userId, build: BUILD, at: Date.now(), state } satisfies Saved);
        if (text.length > MAX_CHARS) continue;      // too big — try again without the bulky ones
        localStorage.setItem(STORE_KEY, text);
        return;
      } catch {
        // Quota exceeded or serialisation failed: fall through to the smaller
        // set, and if that fails too, simply do not persist this time.
      }
    }
  };

  const schedule = () => {
    if (!timer) timer = window.setTimeout(save, 2000);
  };

  const unsubscribe = qc.getQueryCache().subscribe((e) => {
    if (e.type === 'updated' && e.action.type === 'success') schedule();
  });
  const flush = () => { if (timer) save(); };
  const onHide = () => { if (document.visibilityState === 'hidden') save(); };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', onHide);

  return () => {
    unsubscribe();
    window.removeEventListener('pagehide', flush);
    document.removeEventListener('visibilitychange', onHide);
    window.clearTimeout(timer);
  };
}
